// MARK://dhk8/admin-shell/lib/clients/units
// Unidades por-tenant → catalog-pricing (TenantUnitService), por el grafo.
// Decisión Gustavo 2026-06-23: NADA de texto libre en unidades. El usuario elige
// de un catálogo: el catálogo SAT c_ClaveUnidad (2,417 claves, lazy-loaded) +
// FAVORITOS del tenant (claves SAT recurrentes) + unidades CUSTOM (lo que no está
// en SAT). Este cliente cubre la capa por-tenant (favoritos + custom):
//   query  tenantUnits: [TenantUnit!]!
//   mut    setUnitFavorite(unitKey,label,symbol,satClave,favorite) → upsert favorito
//   mut    createCustomUnit(label,symbol,satClave) → unidad custom (isCustom)
//   mut    updateCustomUnit(id,...) / deleteCustomUnit(id)
// El tenant SIEMPRE lo pone el JWT (anti-IDOR / RLS); no va en el input.
import { PREVIEW_STUB, mock } from '../env.js'
import { gqlClient } from '../gql.js'
import {
  AdminTenantUnitsQuery,
  AdminSetUnitFavoriteMutation,
  AdminCreateCustomUnitMutation,
  AdminUpdateCustomUnitMutation,
  AdminDeleteCustomUnitMutation,
  AdminUnitConversionsQuery,
  AdminUpsertUnitConversionMutation,
  AdminDeleteUnitConversionMutation,
} from '../queries/units.queries.js'

export interface TenantUnit {
  id: string
  tenantId: string
  unitKey: string
  label: string
  symbol: string | null
  satClave: string | null
  isCustom: boolean
  isFavorite: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

// Shape que devuelve el grafo (typed-document-node): opcionales como `?` | null.
interface GqlTenantUnit {
  id: string
  tenantId: string
  unitKey: string
  label: string
  symbol?: string | null
  satClave?: string | null
  isCustom: boolean
  isFavorite: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}
function toUi(u: GqlTenantUnit): TenantUnit {
  return {
    id: u.id,
    tenantId: u.tenantId,
    unitKey: u.unitKey,
    label: u.label,
    symbol: u.symbol ?? null,
    satClave: u.satClave ?? null,
    isCustom: u.isCustom,
    isFavorite: u.isFavorite,
    sortOrder: u.sortOrder,
    createdAt: u.createdAt,
    updatedAt: u.updatedAt,
  }
}

// ── preview store (sin backend) ──────────────────────────────────────────────
const db: { units: TenantUnit[] } = {
  units: [
    {
      id: 'tu-demo-gr', tenantId: 'tnt_demo', unitKey: 'GRM', label: 'Gramo', symbol: 'g',
      satClave: 'GRM', isCustom: false, isFavorite: true, sortOrder: 0,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    },
    {
      id: 'tu-demo-pza', tenantId: 'tnt_demo', unitKey: 'H87', label: 'Pieza', symbol: 'pza',
      satClave: 'H87', isCustom: false, isFavorite: true, sortOrder: 1,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    },
    {
      id: 'tu-demo-charola', tenantId: 'tnt_demo', unitKey: 'charola', label: 'Charola', symbol: 'charola',
      satClave: null, isCustom: true, isFavorite: false, sortOrder: 2,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    },
  ],
}
function slug(label: string): string {
  return label.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'unidad'
}

export async function listTenantUnits(signal?: AbortSignal): Promise<TenantUnit[]> {
  if (PREVIEW_STUB) return mock(db.units)
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminTenantUnitsQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.tenantUnits ?? []).map(toUi)
}

export interface SetUnitFavoriteInput {
  unitKey: string
  label: string
  symbol?: string | null
  satClave?: string | null
  favorite: boolean
}
export async function setUnitFavorite(input: SetUnitFavoriteInput): Promise<TenantUnit> {
  if (PREVIEW_STUB) {
    const existing = db.units.find((u) => u.unitKey === input.unitKey)
    if (existing) {
      existing.isFavorite = input.favorite
      existing.label = input.label
      existing.symbol = input.symbol ?? existing.symbol
      existing.satClave = input.satClave ?? existing.satClave
      existing.updatedAt = new Date().toISOString()
      return mock(existing)
    }
    const u: TenantUnit = {
      id: `tu-${Math.random().toString(36).slice(2, 8)}`, tenantId: 'tnt_demo',
      unitKey: input.unitKey, label: input.label, symbol: input.symbol ?? null,
      satClave: input.satClave ?? null, isCustom: false, isFavorite: input.favorite,
      sortOrder: db.units.length, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    }
    db.units.push(u)
    return mock(u)
  }
  const vars: { unitKey: string; label: string; symbol?: string; satClave?: string; favorite: boolean } = {
    unitKey: input.unitKey, label: input.label, favorite: input.favorite,
  }
  if (input.symbol != null && input.symbol !== '') vars.symbol = input.symbol
  if (input.satClave != null && input.satClave !== '') vars.satClave = input.satClave
  const res = await gqlClient.mutation(AdminSetUnitFavoriteMutation, vars).toPromise()
  if (res.error) throw res.error
  const u = res.data?.setUnitFavorite
  if (!u) throw new Error('No se pudo guardar el favorito')
  return toUi(u)
}

export interface CreateCustomUnitInput {
  label: string
  symbol?: string | null
  satClave?: string | null
}
export async function createCustomUnit(input: CreateCustomUnitInput): Promise<TenantUnit> {
  if (PREVIEW_STUB) {
    const u: TenantUnit = {
      id: `tu-${Math.random().toString(36).slice(2, 8)}`, tenantId: 'tnt_demo',
      unitKey: slug(input.label), label: input.label, symbol: input.symbol ?? null,
      satClave: input.satClave ?? null, isCustom: true, isFavorite: false,
      sortOrder: db.units.length, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    }
    db.units.push(u)
    return mock(u)
  }
  const vars: { label: string; symbol?: string; satClave?: string } = { label: input.label }
  if (input.symbol != null && input.symbol !== '') vars.symbol = input.symbol
  if (input.satClave != null && input.satClave !== '') vars.satClave = input.satClave
  const res = await gqlClient.mutation(AdminCreateCustomUnitMutation, vars).toPromise()
  if (res.error) throw res.error
  const u = res.data?.createCustomUnit
  if (!u) throw new Error('No se pudo crear la unidad')
  return toUi(u)
}

export interface UpdateCustomUnitInput {
  label?: string
  symbol?: string | null
  satClave?: string | null
  sortOrder?: number
}
export async function updateCustomUnit(id: string, input: UpdateCustomUnitInput): Promise<TenantUnit> {
  if (PREVIEW_STUB) {
    const u = db.units.find((x) => x.id === id)
    if (!u) throw new Error('Unidad no encontrada')
    if (input.label !== undefined) { u.label = input.label; u.unitKey = slug(input.label) }
    if (input.symbol !== undefined) u.symbol = input.symbol
    if (input.satClave !== undefined) u.satClave = input.satClave
    if (input.sortOrder !== undefined) u.sortOrder = input.sortOrder
    u.updatedAt = new Date().toISOString()
    return mock(u)
  }
  const vars: { id: string; label?: string; symbol?: string | null; satClave?: string | null; sortOrder?: number } = { id }
  if (input.label !== undefined) vars.label = input.label
  if (input.symbol !== undefined) vars.symbol = input.symbol
  if (input.satClave !== undefined) vars.satClave = input.satClave
  if (input.sortOrder !== undefined) vars.sortOrder = input.sortOrder
  const res = await gqlClient.mutation(AdminUpdateCustomUnitMutation, vars).toPromise()
  if (res.error) throw res.error
  const u = res.data?.updateCustomUnit
  if (!u) throw new Error('No se pudo actualizar la unidad')
  return toUi(u)
}

export async function deleteCustomUnit(id: string): Promise<void> {
  if (PREVIEW_STUB) { db.units = db.units.filter((x) => x.id !== id); return mock(undefined) }
  const res = await gqlClient.mutation(AdminDeleteCustomUnitMutation, { id }).toPromise()
  if (res.error) throw res.error
}

// ── Equivalencias (unit_conversions) ─────────────────────────────────────────
// Dueño canónico del grafo = erp-products (canon §2.2): expone `tenantId`
// (null = fila GLOBAL del catálogo, no editable; set = del tenant via RLS). La UI
// trabaja con `global`, que aquí DERIVAMOS de `tenantId == null`. El usuario sólo
// define el factor de su unidad custom/SAT a una BASE (GR/ML/PZA); el motor
// transitivo de erp-inventory convierte el resto.
export interface UnitConversion {
  id: string
  tenantId: string | null
  fromUnit: string
  toUnit: string
  factor: number
  global: boolean
}
interface GqlUnitConversion {
  id: string
  tenantId?: string | null
  fromUnit: string
  toUnit: string
  factor: number
}
function toUiConv(c: GqlUnitConversion): UnitConversion {
  const tenantId = c.tenantId ?? null
  return { id: c.id, tenantId, fromUnit: c.fromUnit, toUnit: c.toUnit, factor: c.factor, global: tenantId == null }
}

// preview store de conversiones (dos globales no editables + una del tenant). El
// flag `global` se deriva de tenantId == null, igual que el grafo real.
const convDb: { conversions: UnitConversion[] } = {
  conversions: [
    { id: 'uc-glob-kg', tenantId: null, fromUnit: 'KG', toUnit: 'GR', factor: 1000, global: true },
    { id: 'uc-glob-l', tenantId: null, fromUnit: 'L', toUnit: 'ML', factor: 1000, global: true },
    { id: 'uc-tnt-charola', tenantId: 'preview-tenant', fromUnit: 'charola', toUnit: 'PZA', factor: 12, global: false },
  ],
}

export async function listUnitConversions(signal?: AbortSignal): Promise<UnitConversion[]> {
  if (PREVIEW_STUB) return mock(convDb.conversions)
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminUnitConversionsQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.unitConversions ?? []).map(toUiConv)
}

/**
 * Resuelve cuántas unidades `to` hay en 1 unidad `from`, usando las equivalencias
 * cargadas (globales SI + custom del tenant). Espeja el motor transitivo del backend
 * (erp-inventory getUnitConversionFactor): directo → vía base (GR/ML/PZA) → un salto.
 * Sirve para que la UI auto-convierta presentaciones a la unidad base SIN que el
 * usuario haga cuentas. Devuelve null si no es resoluble (pedir equivalencia en /units).
 */
const CONV_BASES = ['GR', 'ML', 'PZA']
// Claves SAT c_ClaveUnidad → código operativo (con el que vive unit_conversions).
// Evita el choque "Litro (l)"=LTR vs "Litro (L)"=L al elegir del catálogo SAT.
const SAT_TO_CODE: Record<string, string> = {
  LTR: 'L', MLT: 'ML', KGM: 'KG', GRM: 'GR', MGM: 'MG', ONZ: 'OZ', LBR: 'LB',
  F18: 'FLOZ', GLL: 'GAL', H87: 'PZA', DZN: 'DOC',
}
export function normalizeUnit(u: string): string {
  return SAT_TO_CODE[u] ?? u
}
export function resolveConversionFactor(fromRaw: string, toRaw: string, rows: UnitConversion[]): number | null {
  const from = normalizeUnit(fromRaw), to = normalizeUnit(toRaw)
  if (!from || !to) return null
  if (from === to) return 1
  const edge = new Map<string, number>() // `${a}>${b}` = cuántas b por 1 a
  for (const r of rows) {
    edge.set(`${r.fromUnit}>${r.toUnit}`, r.factor)
    if (r.factor !== 0 && !edge.has(`${r.toUnit}>${r.fromUnit}`)) edge.set(`${r.toUnit}>${r.fromUnit}`, 1 / r.factor)
  }
  const direct = (a: string, b: string): number | null => edge.get(`${a}>${b}`) ?? null
  const toBase = (u: string): { base: string; f: number } | null => {
    if (CONV_BASES.includes(u)) return { base: u, f: 1 }
    for (const b of CONV_BASES) { const d = direct(u, b); if (d != null) return { base: b, f: d } }
    for (const [k, f1] of edge) { // un salto: u → x → base
      const [a, x] = k.split('>')
      if (a !== u) continue
      for (const b of CONV_BASES) { const f2 = direct(x, b); if (f2 != null) return { base: b, f: f1 * f2 } }
    }
    return null
  }
  const d = direct(from, to)
  if (d != null) return d
  const a = toBase(from), b = toBase(to)
  if (a && b && a.base === b.base && b.f !== 0) return a.f / b.f
  return null
}

export interface UpsertUnitConversionInput {
  fromUnit: string
  toUnit: string
  factor: number
}
export async function upsertUnitConversion(input: UpsertUnitConversionInput): Promise<UnitConversion> {
  if (PREVIEW_STUB) {
    const existing = convDb.conversions.find((c) => !c.global && c.fromUnit === input.fromUnit && c.toUnit === input.toUnit)
    if (existing) { existing.factor = input.factor; return mock(existing) }
    const c: UnitConversion = {
      id: `uc-${Math.random().toString(36).slice(2, 8)}`,
      tenantId: 'preview-tenant',
      fromUnit: input.fromUnit, toUnit: input.toUnit, factor: input.factor, global: false,
    }
    convDb.conversions.push(c)
    return mock(c)
  }
  const res = await gqlClient.mutation(AdminUpsertUnitConversionMutation, {
    input: { fromUnit: input.fromUnit, toUnit: input.toUnit, factor: input.factor },
  }).toPromise()
  if (res.error) throw res.error
  const c = res.data?.upsertUnitConversion
  if (!c) throw new Error('No se pudo guardar la equivalencia')
  return toUiConv(c)
}

export async function deleteUnitConversion(id: string): Promise<void> {
  if (PREVIEW_STUB) { convDb.conversions = convDb.conversions.filter((c) => c.id !== id); return mock(undefined) }
  const res = await gqlClient.mutation(AdminDeleteUnitConversionMutation, { id }).toPromise()
  if (res.error) throw res.error
}
